import { useEffect, useState } from 'react';

/**
 * Reflect a single URL search-param as React state. The selected-thread id
 * lives in `?thread=...`. Cheap and bookmarkable.
 */
function readParam(name: string): string | null {
  if (typeof window === 'undefined') return null;
  return new URL(window.location.href).searchParams.get(name);
}

/**
 * R8 — the optional `replace` mode. A high-churn param like the global search
 * (`?q`, updated on every keystroke) should NOT push a new history entry per
 * change — that would bury the previous view under dozens of back-button stops.
 * With `{ replace: true }` the setter rewrites the current entry instead, so the
 * URL stays bookmarkable/shareable without spamming history. Navigation params
 * (thread/contact/view) keep the default push so Back works as expected.
 */
export function useUrlParam(
  name: string,
  options?: { replace?: boolean },
): [string | null, (next: string | null) => void] {
  const replace = options?.replace === true;
  const [value, setValue] = useState<string | null>(() => readParam(name));

  useEffect(() => {
    const onPop = () => setValue(readParam(name));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [name]);

  const setUrlValue = (next: string | null) => {
    const url = new URL(window.location.href);
    if (next === null || next === '') url.searchParams.delete(name);
    else url.searchParams.set(name, next);
    if (replace) window.history.replaceState({}, '', url.toString());
    else window.history.pushState({}, '', url.toString());
    setValue(next);
  };

  return [value, setUrlValue];
}
